import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Check, Sparkles, Crown, Heart, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { useSubscription } from "@/hooks/useSubscription";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { JButton } from "@/components/JButton";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { ManageSubscriptionButton } from "@/components/ManageSubscriptionButton";
import { isNative, platform } from "@/lib/native";
import { purchaseSubscription, restorePurchases } from "@/lib/native/iap";
import { toast } from "sonner";


export const Route = createFileRoute("/_authenticated/app/pricing")({
  component: Pricing,
});

type Cycle = "monthly" | "yearly";
type PaidTier = "family" | "premium";

interface PlanCopy {
  name: { en: string; ar: string };
  tagline: { en: string; ar: string };
  features: { en: string; ar: string }[];
}

const COPY: Record<"free" | PaidTier, PlanCopy> = {
  free: {
    name: { en: "Free", ar: "مجاني" },
    tagline: { en: "A gentle start", ar: "بداية لطيفة" },
    features: [
      { en: "Limited stories & activities", ar: "قصص وأنشطة محدودة" },
      { en: "One child profile", ar: "ملف طفل واحد" },
      { en: "Basic daily challenges", ar: "تحديات يومية أساسية" },
    ],
  },
  family: {
    name: { en: "Family", ar: "العائلة" },
    tagline: { en: "Everything your kids need", ar: "كل ما يحتاجه أطفالك" },
    features: [
      { en: "Full library of stories & activities", ar: "مكتبة كاملة من القصص والأنشطة" },
      { en: "Up to 3 child profiles", ar: "حتى ٣ ملفات أطفال" },
      { en: "Parent dashboard", ar: "لوحة الوالدين" },
      { en: "Rewards & badges system", ar: "نظام مكافآت وشارات" },
      { en: "Offline access", ar: "وصول بدون إنترنت" },
    ],
  },
  premium: {
    name: { en: "Premium", ar: "الذهبية" },
    tagline: { en: "The full Jozori experience", ar: "تجربة جذوري الكاملة" },
    features: [
      { en: "Everything in Family", ar: "كل ميزات العائلة" },
      { en: "Unlimited child profiles", ar: "ملفات أطفال غير محدودة" },
      { en: "Advanced analytics", ar: "تحليلات متقدمة" },
      { en: "Exclusive premium content", ar: "محتوى حصري متميز" },
      { en: "Early access features", ar: "وصول مبكر للميزات الجديدة" },
      { en: "Premium learning experiences", ar: "تجارب تعلم متميزة" },
    ],
  },
};

const PRICES: Record<PaidTier, { monthly: number; yearly: number; monthlyId: string; yearlyId: string }> = {
  family: { monthly: 7.99, yearly: 59, monthlyId: "family_monthly", yearlyId: "family_yearly" },
  premium: { monthly: 14.99, yearly: 119, monthlyId: "premium_monthly", yearlyId: "premium_yearly" },
};

function Pricing() {
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const { subscription, loading, refetch } = useSubscription();
  const { openCheckout, loading: checkoutLoading } = usePaddleCheckout();
  const [cycle, setCycle] = useState<Cycle>("yearly");
  const [pendingTier, setPendingTier] = useState<PaidTier | null>(null);
  const [cycleTouched, setCycleTouched] = useState(false);

  const isAr = lang === "ar";

  // Default the cycle toggle to the user's actual billing cycle once loaded.
  useEffect(() => {
    if (!cycleTouched && subscription?.isActive && subscription.cycle) {
      setCycle(subscription.cycle);
    }
  }, [subscription?.isActive, subscription?.cycle, cycleTouched]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success(isAr ? "تم الاشتراك بنجاح!" : "Subscription successful!");
      window.history.replaceState({}, "", window.location.pathname);
      refetch();
    }
  }, [isAr, refetch]);

  const native = isNative();

  const handleSubscribe = async (tier: PaidTier) => {
    if (!user) return;
    if (pendingTier || checkoutLoading) return; // double-tap guard

    // Block if user already has an active subscription on this tier.
    if (subscription?.isActive && subscription.tier === tier) {
      toast.info(
        isAr ? "اشتراكك في هذه الخطة فعّال بالفعل" : "You're already subscribed to this plan",
      );
      refetch();
      return;
    }

    const priceId = cycle === "monthly" ? PRICES[tier].monthlyId : PRICES[tier].yearlyId;
    try {
      setPendingTier(tier);
      const { Events } = await import("@/lib/analytics");
      Events.checkoutOpen(priceId);
      if (native) {
        // iOS/Android: Apple StoreKit / Google Play Billing only.
        // Never call Paddle/web checkout here — Apple rejects external payment links.
        await purchaseSubscription(tier, cycle);
        toast.success(isAr ? "تم الاشتراك بنجاح!" : "Subscription successful!");
        refetch();
      } else {
        await openCheckout({
          priceId,
          customerEmail: user.email ?? undefined,
          userId: user.id,
          onCompleted: () => {
            toast.success(isAr ? "تم الاشتراك بنجاح!" : "Subscription successful!");
            refetch();
          },
        });
      }
      Events.subscribe(tier, priceId);
    } catch (e) {
      const { Events } = await import("@/lib/analytics");
      Events.paymentFailed(e instanceof Error ? e.message : "unknown");
      toast.error(isAr ? "تعذّر فتح الدفع" : "Could not open checkout");
      console.error(e);
    } finally {
      setPendingTier(null);
    }
  };

  const handleRestore = async () => {
    try {
      await restorePurchases();
      toast.success(isAr ? "تم استرداد المشتريات" : "Purchases restored");
    } catch (e: any) {
      toast.error(e?.message ?? (isAr ? "لا يوجد شيء للاسترداد" : "Nothing to restore"));
    }
  };


  return (
    <div dir={dir} className="min-h-screen bg-background">
      {!native && <PaymentTestModeBanner />}
      <div className="px-5 py-8 max-w-5xl mx-auto">
        <header className="text-center mb-8">
          <p className="font-display-en text-[11px] uppercase tracking-[0.3em] text-primary font-bold mb-2">
            {isAr ? "الاشتراك" : "Subscription"}
          </p>
          <h1
            className={`text-3xl md:text-5xl font-black mb-3 tracking-tight ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {isAr ? "خطة واحدة. مغامرات لا تنتهي." : "One family. Endless adventures."}
          </h1>
          <p className="text-foreground/60 text-sm md:text-base max-w-xl mx-auto">
            {isAr
              ? "اختر الخطة المناسبة لعائلتك. ألغِ في أي وقت."
              : "Pick the plan that fits your family. Cancel anytime."}
          </p>
          {subscription && subscription.tier !== "free" && (
            <SubscriptionStatusCard subscription={subscription} isAr={isAr} lang={lang} />
          )}
        </header>

        <CycleToggle
          cycle={cycle}
          setCycle={(c) => { setCycle(c); setCycleTouched(true); }}
          isAr={isAr}
        />

        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mt-6">
          <PlanCard
            tier="free"
            cycle={cycle}
            lang={lang}
            isCurrent={(subscription?.tier ?? "free") === "free"}
            disabled
          />
          <PlanCard
            tier="family"
            cycle={cycle}
            lang={lang}
            isCurrent={
              subscription?.tier === "family" &&
              !!subscription?.isActive &&
              subscription?.cycle === cycle
            }
            highlight
            loading={loading || (checkoutLoading && pendingTier === "family")}
            disabled={!!pendingTier && pendingTier !== "family"}
            onSelect={() => handleSubscribe("family")}
          />
          <PlanCard
            tier="premium"
            cycle={cycle}
            lang={lang}
            isCurrent={
              subscription?.tier === "premium" &&
              !!subscription?.isActive &&
              subscription?.cycle === cycle
            }
            loading={loading || (checkoutLoading && pendingTier === "premium")}
            disabled={!!pendingTier && pendingTier !== "premium"}
            onSelect={() => handleSubscribe("premium")}
          />
        </div>

        {native && (
          <div className="mt-6 flex justify-center">
            <JButton variant="outline" size="sm" onClick={handleRestore}>
              {isAr ? "استرداد المشتريات" : "Restore purchases"}
            </JButton>
          </div>
        )}

        <p className="text-xs text-center text-foreground/50 mt-8">
          {native
            ? platform() === "ios"
              ? isAr
                ? "الدفع وإدارة الاشتراك عبر Apple. ألغِ من إعدادات Apple ID."
                : "Billing handled by Apple. Cancel anytime in your Apple ID settings."
              : isAr
                ? "الدفع وإدارة الاشتراك عبر Google Play."
                : "Billing handled by Google Play. Cancel anytime in Play subscriptions."
            : isAr
              ? "الدفع آمن عبر Paddle. تطبق الضرائب حسب بلدك."
              : "Secure billing by Paddle. Taxes apply by region."}
        </p>
      </div>
    </div>
  );
}


function CycleToggle({ cycle, setCycle, isAr }: { cycle: Cycle; setCycle: (c: Cycle) => void; isAr: boolean }) {
  return (
    <div className="flex justify-center">
      <div className="inline-flex p-1 bg-clay/30 rounded-full relative">
        <button
          onClick={() => setCycle("monthly")}
          className={`relative z-10 px-5 py-2 text-sm font-bold rounded-full transition-colors ${
            cycle === "monthly" ? "bg-card text-foreground shadow" : "text-foreground/60"
          }`}
        >
          {isAr ? "شهري" : "Monthly"}
        </button>
        <button
          onClick={() => setCycle("yearly")}
          className={`relative z-10 px-5 py-2 text-sm font-bold rounded-full transition-colors inline-flex items-center gap-2 ${
            cycle === "yearly" ? "bg-card text-foreground shadow" : "text-foreground/60"
          }`}
        >
          {isAr ? "سنوي" : "Yearly"}
          <span className="bg-primary text-primary-foreground text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
            {isAr ? "وفّر ٢٧٪" : "Save 27%"}
          </span>
        </button>
      </div>
    </div>
  );
}

function PlanCard({
  tier,
  cycle,
  lang,
  isCurrent,
  highlight,
  loading,
  disabled,
  onSelect,
}: {
  tier: "free" | PaidTier;
  cycle: Cycle;
  lang: "en" | "ar";
  isCurrent: boolean;
  highlight?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
}) {
  const isAr = lang === "ar";
  const copy = COPY[tier];

  const { displayPrice, period, savingsLabel, perMonth } = useMemo(() => {
    if (tier === "free") {
      return { displayPrice: isAr ? "مجاناً" : "Free", period: "", savingsLabel: null, perMonth: null };
    }
    const p = PRICES[tier];
    if (cycle === "monthly") {
      return {
        displayPrice: `$${p.monthly}`,
        period: isAr ? "/شهر" : "/mo",
        savingsLabel: null,
        perMonth: null,
      };
    }
    const monthlyEquivalent = (p.yearly / 12).toFixed(2);
    const fullYear = (p.monthly * 12).toFixed(2);
    const savings = (p.monthly * 12 - p.yearly).toFixed(0);
    return {
      displayPrice: `$${p.yearly}`,
      period: isAr ? "/سنة" : "/yr",
      savingsLabel: isAr
        ? `وفّر $${savings} مقابل $${fullYear}`
        : `Save $${savings} vs $${fullYear}`,
      perMonth: isAr ? `≈ $${monthlyEquivalent} شهرياً` : `≈ $${monthlyEquivalent}/mo`,
    };
  }, [tier, cycle, isAr]);

  const Icon = tier === "premium" ? Crown : tier === "family" ? Heart : Sparkles;

  return (
    <div
      className={`relative rounded-3xl p-6 border-2 transition-all flex flex-col ${
        highlight
          ? "bg-gradient-to-br from-primary/5 via-card to-card border-primary shadow-2xl md:scale-[1.03] md:-translate-y-1"
          : "bg-card border-clay/40 shadow-lg"
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">
          {isAr ? "الأكثر شعبية" : "Most Popular"}
        </span>
      )}
      {isCurrent && (
        <span className="absolute top-4 right-4 bg-secondary text-secondary-foreground text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider">
          {isAr ? "حالياً" : "Current"}
        </span>
      )}

      <div className={`w-11 h-11 rounded-2xl mb-4 inline-flex items-center justify-center ${
        highlight ? "bg-primary text-primary-foreground" : tier === "premium" ? "bg-secondary text-secondary-foreground" : "bg-clay/40 text-foreground"
      }`}>
        <Icon className="w-5 h-5" />
      </div>

      <h3 className={`text-2xl font-black mb-1 ${isAr ? "font-display-ar" : "font-display-en"}`}>
        {copy.name[lang]}
      </h3>
      <p className="text-sm text-foreground/60 mb-5">{copy.tagline[lang]}</p>

      <div className="mb-5">
        <div className="flex items-baseline gap-1">
          <span className={`text-4xl font-black ${highlight ? "text-primary" : "text-foreground"}`}>
            {displayPrice}
          </span>
          {period && <span className="text-foreground/60 text-sm font-bold">{period}</span>}
        </div>
        {perMonth && <p className="text-xs text-foreground/50 mt-1">{perMonth}</p>}
        {savingsLabel && (
          <p className="text-xs font-bold text-secondary mt-1 inline-block bg-secondary/10 px-2 py-0.5 rounded-full">
            {savingsLabel}
          </p>
        )}
      </div>

      <ul className="space-y-2.5 mb-6 flex-1">
        {copy.features.map((f, i) => (
          <li key={i} className="flex gap-2 items-start text-sm">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-primary" : "text-secondary"}`} />
            <span>{f[lang]}</span>
          </li>
        ))}
      </ul>

      {tier === "free" ? (
        <JButton variant="outline" size="md" disabled className="w-full">
          {isCurrent ? (isAr ? "خطتك الحالية" : "Current plan") : (isAr ? "ابدأ مجاناً" : "Start free")}
        </JButton>
      ) : (
        <JButton
          variant={highlight ? "primary" : "secondary"}
          size="md"
          onClick={onSelect}
          disabled={loading || isCurrent || disabled}
          className="w-full"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isCurrent ? (
            isAr ? "اشتراكك فعّال" : "Already active"
          ) : disabled ? (
            isAr ? "جاري المعالجة…" : "Processing…"
          ) : (
            isAr ? "اشترك الآن" : "Subscribe"
          )}
        </JButton>
      )}
    </div>
  );
}

function SubscriptionStatusCard({
  subscription,
  isAr,
  lang,
}: {
  subscription: NonNullable<ReturnType<typeof useSubscription>["subscription"]>;
  isAr: boolean;
  lang: "en" | "ar";
}) {
  const tier = subscription.tier as PaidTier;
  const planName = COPY[tier]?.name[lang] ?? tier;
  const cycleLabel =
    subscription.cycle === "monthly"
      ? isAr ? "شهري" : "Monthly"
      : subscription.cycle === "yearly"
        ? isAr ? "سنوي" : "Yearly"
        : "";

  const days = subscription.daysRemaining;
  const end = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
  const dateStr = end
    ? end.toLocaleDateString(isAr ? "ar" : "en", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // Status badge
  const status = subscription.status;
  const cancelling = subscription.cancelAtPeriodEnd;
  let badgeText: string;
  let badgeClass: string;
  if (status === "trialing") {
    badgeText = isAr ? "فترة تجريبية" : "Trial";
    badgeClass = "bg-secondary/15 text-secondary";
  } else if (status === "past_due") {
    badgeText = isAr ? "دفع متأخر" : "Past due";
    badgeClass = "bg-destructive/15 text-destructive";
  } else if (cancelling || status === "canceled") {
    badgeText = isAr ? "ملغى — ينتهي قريباً" : "Canceled — ends soon";
    badgeClass = "bg-clay/40 text-foreground/70";
  } else {
    badgeText = isAr ? "فعّال" : "Active";
    badgeClass = "bg-primary/15 text-primary";
  }

  const renewLabel = cancelling || status === "canceled"
    ? (isAr ? "ينتهي في" : "Ends on")
    : (isAr ? "يتجدد في" : "Renews on");

  return (
    <div className="mt-5 mx-auto max-w-md bg-card border-2 border-primary/30 rounded-2xl p-5 shadow-lg text-start">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Crown className="w-4 h-4 text-secondary shrink-0" />
          <h2 className={`text-lg font-black truncate ${isAr ? "font-display-ar" : "font-display-en"}`}>
            {planName}
            {cycleLabel && <span className="text-foreground/60 font-bold"> · {cycleLabel}</span>}
          </h2>
        </div>
        <span className={`text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider whitespace-nowrap ${badgeClass}`}>
          {badgeText}
        </span>
      </div>

      {days !== null && (
        <p className="text-2xl font-black text-foreground mb-1">
          {isAr
            ? `${days} يوم${days === 1 ? "" : days === 2 ? "ان" : days <= 10 ? " متبقية" : " متبقياً"}`
            : `${days} ${days === 1 ? "day" : "days"} remaining`}
        </p>
      )}
      {dateStr && (
        <p className="text-sm text-foreground/60 mb-4">
          {renewLabel} {dateStr}
        </p>
      )}

      <ManageSubscriptionButton size="sm" />
    </div>
  );
}
